/**
 * FeatureFlagsPanel — Feature Flags Admin (Tier 3)
 * 
 * Shows all feature flags with toggle switches.
 * Integrates into CognitiveLab as a tab.
 */
import React, { useState, useEffect, useCallback } from 'react';

interface FeatureFlag {
  key: string;
  enabled: boolean;
  scope: string;
  scopeId: string;
  metadata: any;
  updatedAt: string;
}

interface Props {
  getToken: () => Promise<string>;
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const FLAG_ICONS: Record<string, string> = {
  web_search: '🌐',
  extended_thinking: '🧠',
  voice_mode: '🎤',
  file_attachments: '📎',
  browser_control: '🖥️',
  auto_skill_detection: '🔮',
};

export const FeatureFlagsPanel: React.FC<Props> = ({ getToken }) => {
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null);

  const loadFlags = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/api/v1/feature-flags`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setFlags(data.flags || []);
      }
    } catch (e) {
      console.error('Failed to load feature flags:', e);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { loadFlags(); }, [loadFlags]);

  const toggleFlag = async (key: string, currentEnabled: boolean) => {
    setToggling(key);
    try {
      const token = await getToken();
      await fetch(`${API_BASE}/api/v1/feature-flags/${key}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: !currentEnabled }),
      });
      await loadFlags();
    } catch (e) {
      console.error('Toggle failed:', e);
    } finally {
      setToggling(null);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Separate global from scoped flags
  const globalFlags = flags.filter(f => f.scope === 'global');

  return (
    <div className="flex-1 p-6 overflow-auto">
      <div className="max-w-3xl mx-auto space-y-5">

        {/* ── Header ── */}
        <div className="flex items-center gap-3 mb-2">
          <span className="text-2xl">🎛️</span>
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Feature Flags</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Toggle platform capabilities · {flags.length} flag{flags.length !== 1 ? 's' : ''} configured
            </p>
          </div>
        </div>

        {/* ── Global Flags ── */}
        <div className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm border border-slate-200 dark:border-slate-700/50 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700/50">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Global Flags
            </h3>
          </div>
          <div className="divide-y divide-slate-100 dark:divide-slate-700/30">
            {globalFlags.map(flag => {
              const icon = FLAG_ICONS[flag.key] || '⚙️';
              const desc = flag.metadata?.description || '';
              const requiresKey = flag.metadata?.requiresKey;
              const isToggling = toggling === flag.key;

              return (
                <div key={flag.key} className="flex items-center justify-between px-5 py-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="text-xl">{icon}</span>
                    <div>
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-semibold text-slate-900 dark:text-white font-mono">
                          {flag.key}
                        </code>
                        {requiresKey && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-600/40 font-mono">
                            requires: {requiresKey}
                          </span>
                        )}
                      </div>
                      {desc && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{desc}</p>
                      )}
                    </div>
                  </div>

                  {/* Toggle Switch */}
                  <button
                    onClick={() => toggleFlag(flag.key, flag.enabled)}
                    disabled={isToggling}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                      flag.enabled
                        ? 'bg-indigo-600'
                        : 'bg-slate-300 dark:bg-slate-600'
                    } ${isToggling ? 'opacity-50' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 shadow-sm ${
                        flag.enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Scope Resolution Info ── */}
        <div className="bg-slate-50/80 dark:bg-slate-800/30 border border-slate-200 dark:border-slate-700/30 rounded-xl p-4">
          <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2">Flag Resolution Priority</h4>
          <div className="grid grid-cols-3 gap-3 text-center">
            {[
              { level: '1', label: 'User Override', desc: 'Highest priority', icon: '👤' },
              { level: '2', label: 'Workspace', desc: 'Team-level setting', icon: '🏢' },
              { level: '3', label: 'Global Default', desc: 'Platform baseline', icon: '🌍' },
            ].map(s => (
              <div key={s.level} className="space-y-1">
                <span className="text-xl block">{s.icon}</span>
                <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">{s.label}</p>
                <p className="text-[10px] text-slate-400 dark:text-slate-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureFlagsPanel;
